import { useParams } from "react-router-dom";
import { 
  Rocket, Target, Megaphone, Bot, Users, Lightbulb, ShieldCheck, Shield,
  Truck, HardHat, Wrench
} from "lucide-react";
import LabTemplate from "@/components/prolab/LabTemplate";
import LAB_CONFIGS from "@/data/labConfigs";

// Icon mapping for labs
const LAB_ICONS = {
  "ai-entrepreneurship": Rocket,
  "project-management": Target,
  "sales-marketing": Megaphone,
  "automation": Bot,
  "leadership": Users,
  "communication": Lightbulb,
  "tqm": ShieldCheck,
  "quality-auditing": Target,
  "osha-safety": Shield,
  "safety-management": ShieldCheck,
  "logistics": Truck,
  "construction": HardHat,
  "electrician": Wrench,
  "security-level-2": Shield,
  "security-level-3": Shield,
  "security-level-4": Shield
};

/**
 * Generic Lab Page - Renders any lab using the LabTemplate
 * Uses labId from URL params to load the correct configuration
 */
export default function LabPage() {
  const { labId } = useParams();
  const config = LAB_CONFIGS[labId];

  if (!config) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#991B1B] mb-2">Lab Not Found</h1>
          <p className="text-[#1E3A8A]">The requested lab could not be found.</p>
        </div>
      </div>
    );
  }

  // Get the icon for this lab
  const LabIcon = LAB_ICONS[labId] || Rocket;

  // Sample progress data (would come from auth system in production)
  const progressData = {
    completedMissions: [config.missions[0]?.id].filter(Boolean),
    userOutputs: config.portfolioOutputs.slice(0, 1),
    readinessScore: 15,
    recommendedLabs: getRecommendedLabs(labId)
  };

  return (
    <LabTemplate 
      {...config}
      labIcon={LabIcon}
      completedMissions={progressData.completedMissions}
      userOutputs={progressData.userOutputs}
      readinessScore={progressData.readinessScore}
      recommendedLabs={progressData.recommendedLabs}
    />
  );
}

// Helper to get recommended labs based on current lab
function getRecommendedLabs(currentLabId) {
  const recommendations = {
    "ai-entrepreneurship": [
      { name: "Project Management AI Lab", reason: "Manage your venture development", href: "/juswork/prolab/project-management", color: "#2563EB" },
      { name: "Sales & Marketing AI Lab", reason: "Scale your customer acquisition", href: "/juswork/prolab/sales-marketing", color: "#3B82F6" }
    ],
    "project-management": [
      { name: "Leadership AI Lab", reason: "Develop team leadership skills", href: "/juswork/prolab/leadership", color: "#1E3A8A" },
      { name: "Communication AI Lab", reason: "Improve stakeholder communication", href: "/juswork/prolab/communication", color: "#2563EB" }
    ],
    "sales-marketing": [
      { name: "Automation AI Lab", reason: "Automate marketing processes", href: "/juswork/prolab/automation", color: "#60A5FA" },
      { name: "Communication AI Lab", reason: "Improve sales communication", href: "/juswork/prolab/communication", color: "#2563EB" }
    ],
    "automation": [
      { name: "Project Management AI Lab", reason: "Manage automation projects", href: "/juswork/prolab/project-management", color: "#2563EB" },
      { name: "TQM AI Lab", reason: "Ensure quality in automation", href: "/juswork/prolab/tqm", color: "#1E3A8A" }
    ],
    "leadership": [
      { name: "Communication AI Lab", reason: "Enhance leadership communication", href: "/juswork/prolab/communication", color: "#2563EB" },
      { name: "Project Management AI Lab", reason: "Lead project teams", href: "/juswork/prolab/project-management", color: "#2563EB" }
    ],
    "communication": [
      { name: "Leadership AI Lab", reason: "Apply communication in leadership", href: "/juswork/prolab/leadership", color: "#1E3A8A" },
      { name: "Sales & Marketing AI Lab", reason: "Use communication for sales", href: "/juswork/prolab/sales-marketing", color: "#3B82F6" }
    ],
    "tqm": [
      { name: "Quality Auditing AI Lab", reason: "Audit your quality systems", href: "/juswork/prolab/quality-auditing", color: "#2563EB" },
      { name: "Safety Management AI Lab", reason: "Integrate safety with quality", href: "/juswork/prolab/safety-management", color: "#60A5FA" }
    ],
    "quality-auditing": [
      { name: "TQM AI Lab", reason: "Build quality management systems", href: "/juswork/prolab/tqm", color: "#1E3A8A" },
      { name: "OSHA & Safety AI Lab", reason: "Audit safety compliance", href: "/juswork/prolab/osha-safety", color: "#3B82F6" }
    ],
    "osha-safety": [
      { name: "Safety Management AI Lab", reason: "Build comprehensive safety programs", href: "/juswork/prolab/safety-management", color: "#60A5FA" },
      { name: "Quality Auditing AI Lab", reason: "Audit safety systems", href: "/juswork/prolab/quality-auditing", color: "#2563EB" }
    ],
    "safety-management": [
      { name: "TQM AI Lab", reason: "Integrate quality and safety", href: "/juswork/prolab/tqm", color: "#1E3A8A" },
      { name: "Leadership AI Lab", reason: "Lead safety culture", href: "/juswork/prolab/leadership", color: "#1E3A8A" }
    ],
    "logistics": [
      { name: "Automation AI Lab", reason: "Automate logistics processes", href: "/juswork/prolab/automation", color: "#60A5FA" },
      { name: "Project Management AI Lab", reason: "Manage logistics projects", href: "/juswork/prolab/project-management", color: "#2563EB" }
    ],
    "construction": [
      { name: "Safety Management AI Lab", reason: "Manage construction safety", href: "/juswork/prolab/safety-management", color: "#60A5FA" },
      { name: "Project Management AI Lab", reason: "Manage construction projects", href: "/juswork/prolab/project-management", color: "#2563EB" }
    ],
    "electrician": [
      { name: "OSHA & Safety AI Lab", reason: "Ensure electrical safety", href: "/juswork/prolab/osha-safety", color: "#3B82F6" },
      { name: "Quality Auditing AI Lab", reason: "Audit electrical work", href: "/juswork/prolab/quality-auditing", color: "#2563EB" }
    ],
    "security-level-2": [
      { name: "Security Level III AI Lab", reason: "Advance to supervision", href: "/juswork/prolab/security-level-3", color: "#7F1D1D" },
      { name: "Communication AI Lab", reason: "Improve security communication", href: "/juswork/prolab/communication", color: "#2563EB" }
    ],
    "security-level-3": [
      { name: "Security Level IV AI Lab", reason: "Advance to executive security", href: "/juswork/prolab/security-level-4", color: "#450A0A" },
      { name: "Leadership AI Lab", reason: "Develop leadership skills", href: "/juswork/prolab/leadership", color: "#1E3A8A" }
    ],
    "security-level-4": [
      { name: "Leadership AI Lab", reason: "Strategic leadership", href: "/juswork/prolab/leadership", color: "#1E3A8A" },
      { name: "Project Management AI Lab", reason: "Manage security programs", href: "/juswork/prolab/project-management", color: "#2563EB" }
    ]
  };

  return recommendations[currentLabId] || [];
}
